import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Chess } from 'chess.js';
import { useSearchParams } from 'react-router-dom';
import { API_BASE } from '../api';
import { useDataCache } from '../DataCacheContext';
import InteractiveBoard, { playSound, getSoundType } from './InteractiveBoard';

export default function BlunderReviewPanel({ c, pieceSet, boardTheme, setPieceSet, setBoardTheme }) {
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username') || '';
  const cache = useDataCache();

  // ── Category toggle ──
  const [category, setCategory] = useState('blunder');

  // ── Data fetching ──
  const [loading, setLoading] = useState(true);
  const [blunders, setBlunders] = useState([]);
  const [error, setError] = useState(null);
  const [fetchAttempt, setFetchAttempt] = useState(0);

  // ── Analysis progress ──
  const [analysisStatus, setAnalysisStatus] = useState(null);

  useEffect(() => {
    if (!username) { setLoading(false); setError('No username found'); return; }

    const cacheKey = `blunders:${category}:${username}`;
    const cached = cache.get(cacheKey);
    if (cached) { setBlunders(cached); setLoading(false); setError(null); return; }

    setLoading(true);
    setError(null);

    fetch(`${API_BASE}/blunders?username=${encodeURIComponent(username)}&category=${category}`)
      .then(r => { if (!r.ok) throw new Error(`Failed to load blunders (${r.status})`); return r.json(); })
      .then(data => {
        cache.set(cacheKey, data);
        setBlunders(data);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [username, category, fetchAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll analysis status
  useEffect(() => {
    if (!username) return;
    const checkStatus = () => {
      fetch(`${API_BASE}/analysis-status?username=${encodeURIComponent(username)}`)
        .then(r => r.json())
        .then(setAnalysisStatus)
        .catch(() => {});
    };
    checkStatus();
    const interval = setInterval(checkStatus, 4000);
    return () => clearInterval(interval);
  }, [username]);

  // Auto-refresh when analysis completes and we have no data
  useEffect(() => {
    if (analysisStatus?.status === 'completed' && blunders.length === 0 && !loading) {
      cache.set(`blunders:${category}:${username}`, undefined);
      setFetchAttempt(a => a + 1);
    }
  }, [analysisStatus?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Color filter ──
  const [colorFilter, setColorFilter] = useState('white');
  const filteredBlunders = useMemo(() => {
    return blunders.filter(b => b.playerColor === colorFilter);
  }, [blunders, colorFilter]);

  // ── Selected blunder ──
  const [selectedBlunder, setSelectedBlunder] = useState(null);
  const [blunderIndex, setBlunderIndex] = useState(0);

  // Auto-select first blunder on load
  useEffect(() => {
    if (filteredBlunders.length > 0 && !selectedBlunder) {
      setSelectedBlunder(filteredBlunders[0]);
      setBlunderIndex(0);
    }
  }, [filteredBlunders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset selection on category or color change
  useEffect(() => {
    setSelectedBlunder(null);
    setBlunderIndex(0);
  }, [category, colorFilter]);

  // ── Board state ──
  const [chess] = useState(() => new Chess());
  const [history, setHistory] = useState([]);
  const [moveIndex, setMoveIndex] = useState(0);
  const [positionsArr, setPositionsArr] = useState([]);
  const [flipped, setFlipped] = useState(false);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [lastMove, setLastMove] = useState(null);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragPos, setDragPos] = useState(null);
  const [dragPiece, setDragPiece] = useState(null);
  const [hoveredSquare, setHoveredSquare] = useState(null);
  const [arrows, setArrows] = useState([]);
  const [highlightedSquares, setHighlightedSquares] = useState(new Set());
  const [pendingPromotion, setPendingPromotion] = useState(null);

  // ── Puzzle feedback (tiered) ──
  const [puzzleFeedback, setPuzzleFeedback] = useState(null);
  const [pStats, setPStats] = useState({ best: 0, ok: 0, missed: 0, total: 0 });
  const [puzzleStartIdx, setPuzzleStartIdx] = useState(0);

  // Load puzzle position
  useEffect(() => {
    if (!selectedBlunder) return;
    const preceding = selectedBlunder.precedingMoves || [];
    const tempChess = new Chess();
    const positions = [tempChess.fen()];
    const hist = [];
    for (const san of preceding) {
      try {
        const result = tempChess.move(san);
        if (result) { hist.push(result); positions.push(tempChess.fen()); }
        else break;
      } catch { break; }
    }
    chess.load(selectedBlunder.fen);
    const startIdx = positions.length - 1;
    setHistory(hist); setPositionsArr(positions); setMoveIndex(startIdx);
    setPuzzleStartIdx(startIdx);
    setSelectedSquare(null); setLegalMoves([]);
    if (hist.length > 0) setLastMove({ from: hist[hist.length - 1].from, to: hist[hist.length - 1].to }); else setLastMove(null);
    setArrows([]); setHighlightedSquares(new Set()); setPendingPromotion(null);
    setPuzzleFeedback(null);
    setFlipped(selectedBlunder.playerColor === 'black');
  }, [selectedBlunder]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentFen = positionsArr[moveIndex] || chess.fen();
  const boardChess = useMemo(() => { const bc = new Chess(); bc.load(currentFen); return bc; }, [currentFen]);
  const squareName = useCallback((ri, ci) => { const f = flipped ? 7 - ci : ci; const r = flipped ? ri : 7 - ri; return 'abcdefgh'[f] + (r + 1); }, [flipped]);

  // ── Drag-and-drop ──
  const getSquareFromPoint = useCallback((clientX, clientY) => {
    const boardEl = document.querySelector('[data-board-grid]');
    if (!boardEl) return null;
    const rect = boardEl.getBoundingClientRect();
    const x = clientX - rect.left, y = clientY - rect.top;
    if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) return null;
    const col = Math.floor((x / rect.width) * 8), row = Math.floor((y / rect.height) * 8);
    return 'abcdefgh'[flipped ? 7 - col : col] + ((flipped ? row : 7 - row) + 1);
  }, [flipped]);

  const handleDragStart = useCallback((ri, ci, e) => {
    if (puzzleFeedback || moveIndex !== positionsArr.length - 1) return;
    const sq = squareName(ri, ci); const piece = boardChess.get(sq);
    if (!piece || piece.color !== boardChess.turn()) return;
    e.preventDefault();
    const cX = e.touches ? e.touches[0].clientX : e.clientX; const cY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragFrom(sq); setDragPiece(piece); setDragPos({ x: cX, y: cY });
    setSelectedSquare(sq); setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to));
  }, [moveIndex, positionsArr.length, squareName, boardChess, puzzleFeedback]);

  const isPromotion = useCallback((from, to) => { const p = boardChess.get(from); if (!p || p.type !== 'p') return false; return (p.color === 'w' && to[1] === '8') || (p.color === 'b' && to[1] === '1'); }, [boardChess]);

  const executeMove = useCallback((from, to, promotion = 'q') => {
    try {
      const tempChess = new Chess(currentFen);
      const result = tempChess.move({ from, to, promotion });
      if (result) {
        const nh = [...history.slice(0, moveIndex), result];
        const np = [...positionsArr.slice(0, moveIndex + 1), tempChess.fen()];
        chess.load(tempChess.fen()); setHistory(nh); setPositionsArr(np); setMoveIndex(np.length - 1);
        setLastMove({ from: result.from, to: result.to }); setSelectedSquare(null); setLegalMoves([]);
        playSound(getSoundType(result));
        // Tiered blunder feedback
        if (moveIndex === puzzleStartIdx && !puzzleFeedback && selectedBlunder) {
          const isBest = result.san === selectedBlunder.bestMove;
          const isRepeat = result.san === selectedBlunder.userActualMove;
          let tier;
          if (isBest) tier = 'best';
          else if (isRepeat) tier = 'missed';
          else tier = 'ok';
          setPuzzleFeedback({ tier, movePlayed: result.san, bestMove: selectedBlunder.bestMove, userActualMove: selectedBlunder.userActualMove });
          setPStats(s => ({
            best: s.best + (tier === 'best' ? 1 : 0),
            ok: s.ok + (tier === 'ok' ? 1 : 0),
            missed: s.missed + (tier === 'missed' ? 1 : 0),
            total: s.total + 1,
          }));
        }
        return true;
      }
    } catch {}
    return false;
  }, [currentFen, chess, history, positionsArr, moveIndex, puzzleFeedback, selectedBlunder, puzzleStartIdx]);

  const handlePromotionChoice = useCallback((pt) => { if (!pendingPromotion) return; executeMove(pendingPromotion.from, pendingPromotion.to, pt); setPendingPromotion(null); }, [pendingPromotion, executeMove]);

  useEffect(() => {
    if (!dragFrom) return;
    const onMove = (e) => { setDragPos({ x: e.touches ? e.touches[0].clientX : e.clientX, y: e.touches ? e.touches[0].clientY : e.clientY }); };
    const onEnd = (e) => {
      const cX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX; const cY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
      const dropSq = getSquareFromPoint(cX, cY);
      if (dropSq && dropSq !== dragFrom) {
        if (isPromotion(dragFrom, dropSq)) { setPendingPromotion({ from: dragFrom, to: dropSq, color: boardChess.get(dragFrom).color }); setDragFrom(null); setDragPiece(null); setDragPos(null); return; }
        executeMove(dragFrom, dropSq);
      }
      setDragFrom(null); setDragPiece(null); setDragPos(null);
    };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false }); window.addEventListener('touchend', onEnd);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onEnd); window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onEnd); };
  }, [dragFrom, getSquareFromPoint, isPromotion, executeMove, boardChess]);

  const handleSquareClick = useCallback((ri, ci) => {
    if (puzzleFeedback || moveIndex !== positionsArr.length - 1) return;
    const sq = squareName(ri, ci); const piece = boardChess.get(sq);
    if (selectedSquare) {
      if (boardChess.moves({ square: selectedSquare, verbose: true }).some(m => m.to === sq)) {
        if (isPromotion(selectedSquare, sq)) { setPendingPromotion({ from: selectedSquare, to: sq, color: boardChess.get(selectedSquare).color }); return; }
        executeMove(selectedSquare, sq); return;
      }
      if (piece && piece.color === boardChess.turn()) { setSelectedSquare(sq); setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to)); return; }
      setSelectedSquare(null); setLegalMoves([]); return;
    }
    if (piece && piece.color === boardChess.turn()) { setSelectedSquare(sq); setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to)); }
  }, [selectedSquare, boardChess, moveIndex, positionsArr.length, squareName, isPromotion, executeMove, puzzleFeedback]);

  const goTo = useCallback((idx) => {
    const cl = Math.max(0, Math.min(idx, positionsArr.length - 1));
    setMoveIndex(cl); setSelectedSquare(null); setLegalMoves([]);
    if (cl > 0 && history[cl - 1]) setLastMove({ from: history[cl - 1].from, to: history[cl - 1].to }); else setLastMove(null);
  }, [positionsArr, history]);

  const handleUndo = useCallback(() => {
    if (moveIndex <= 0) return;
    const nh = history.slice(0, moveIndex - 1), np = positionsArr.slice(0, moveIndex);
    chess.load(np[np.length - 1]); setHistory(nh); setPositionsArr(np); setMoveIndex(np.length - 1);
    setSelectedSquare(null); setLegalMoves([]);
    if (nh.length > 0) setLastMove({ from: nh[nh.length - 1].from, to: nh[nh.length - 1].to }); else setLastMove(null);
    if (moveIndex === puzzleStartIdx + 1) setPuzzleFeedback(null);
  }, [chess, history, positionsArr, moveIndex, puzzleStartIdx]);

  // Navigation helpers
  const goToNext = useCallback(() => {
    if (blunderIndex < filteredBlunders.length - 1) {
      const next = blunderIndex + 1;
      setBlunderIndex(next);
      setSelectedBlunder(filteredBlunders[next]);
    }
  }, [blunderIndex, filteredBlunders]);

  const goToPrev = useCallback(() => { // eslint-disable-line no-unused-vars
    if (blunderIndex > 0) {
      const prev = blunderIndex - 1;
      setBlunderIndex(prev);
      setSelectedBlunder(filteredBlunders[prev]);
    }
  }, [blunderIndex, filteredBlunders]);

  // Derived
  const turnColor = boardChess.turn();
  const movePairs = []; for (let i = 0; i < history.length; i += 2) movePairs.push({ num: Math.floor(i/2)+1, white: history[i], black: history[i+1]||null, whiteIdx: i+1, blackIdx: i+2 });

  const cardSx = { px:2.5, py:2, borderRadius:'16px', bgcolor:`${c.textMuted}06`, border:`1px solid ${c.textMuted}10` };
  const labelSx = { color:c.textMuted, fontSize:'0.8rem', fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', mb:1.5 };

  // ── Analysis progress bar ──
  const isAnalyzing = analysisStatus?.status === 'running';
  const progressPct = analysisStatus && analysisStatus.gamesTotal > 0
    ? Math.round((analysisStatus.gamesAnalyzed / analysisStatus.gamesTotal) * 100)
    : 0;

  // ═══ LOADING STATE ═══
  if (loading && blunders.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${c.accent}20`, borderTopColor: c.accent, animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mt: 2 }}>Loading blunder analysis...</Typography>
      </Box>
    );
  }

  if (error && blunders.length === 0) {
    return (
      <Box sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
        <Typography sx={{ color: c.accent, fontSize: '1rem', fontWeight: 700, mb: 1 }}>Could not load blunders</Typography>
        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mb: 2 }}>{error}</Typography>
        <Box onClick={() => setFetchAttempt(a => a + 1)} sx={{
          display: 'inline-flex', alignItems: 'center', gap: 1, px: 2.5, py: 1,
          borderRadius: '10px', cursor: 'pointer', bgcolor: `${c.accent}12`, border: `1.5px solid ${c.accent}30`,
          transition: 'all 0.15s ease', '&:hover': { bgcolor: `${c.accent}20` },
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c.accent} strokeWidth="2" strokeLinecap="round"><path d="M23 4v6h-6" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" /></svg>
          <Typography sx={{ color: c.accent, fontSize: '0.85rem', fontWeight: 700 }}>Retry</Typography>
        </Box>
      </Box>
    );
  }

  // ═══ LEFT PANEL: blunder list ═══
  const blunderList = (
    <Box sx={{ minWidth: 0, overflow: 'auto', maxHeight: { lg: 'calc(100vh - 160px)' }, pr: { lg: 2 } }}>
      {/* Category toggle */}
      <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, borderRadius: '12px', bgcolor: `${c.textMuted}08`, mb: 2.5 }}>
        {[
          { key: 'blunder', label: 'Blunders', symbol: '??' },
          { key: 'mistake', label: 'Mistakes', symbol: '?!' },
        ].map(cat => (
          <Box key={cat.key} onClick={() => setCategory(cat.key)} sx={{
            flex: 1, py: 1.2, borderRadius: '10px', cursor: 'pointer', textAlign: 'center',
            bgcolor: category === cat.key ? `${c.accent}15` : 'transparent',
            border: `1.5px solid ${category === cat.key ? c.accent + '30' : 'transparent'}`,
            transition: 'all 0.15s ease',
            '&:hover': { bgcolor: category === cat.key ? `${c.accent}15` : `${c.textMuted}06` },
          }}>
            <Typography sx={{ color: category === cat.key ? c.accent : c.textMuted, fontSize: '0.9rem', fontWeight: 700 }}>
              {cat.label} <span style={{ opacity: 0.6 }}>{cat.symbol}</span>
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Color filter */}
      <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb:2 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:0.5, p:0.5, borderRadius:'50px', bgcolor:`${c.textMuted}08` }}>
          <Box onClick={() => { if (colorFilter !== 'white') { setColorFilter('white'); setSelectedBlunder(null); setBlunderIndex(0); } }} sx={{ width:40, height:40, borderRadius:'50%', bgcolor:'#ECE2DB', cursor:'pointer', transition:'all 0.15s ease', opacity: colorFilter === 'white' ? 1 : 0.3, transform: colorFilter === 'white' ? 'scale(1)' : 'scale(0.85)', '&:hover':{ opacity: colorFilter === 'white' ? 1 : 0.6 } }} />
          <Box onClick={() => { setColorFilter(colorFilter === 'white' ? 'black' : 'white'); setSelectedBlunder(null); setBlunderIndex(0); }} sx={{ cursor:'pointer', display:'flex', px:0.3, transition:'all 0.15s ease', '&:hover':{ transform:'scale(1.2)' } }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c.textMuted} strokeWidth="2" strokeLinecap="round"><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 014-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 01-4 4H3" /></svg>
          </Box>
          <Box onClick={() => { if (colorFilter !== 'black') { setColorFilter('black'); setSelectedBlunder(null); setBlunderIndex(0); } }} sx={{ width:40, height:40, borderRadius:'50%', bgcolor:'#111115', cursor:'pointer', transition:'all 0.15s ease', opacity: colorFilter === 'black' ? 1 : 0.3, transform: colorFilter === 'black' ? 'scale(1)' : 'scale(0.85)', '&:hover':{ opacity: colorFilter === 'black' ? 1 : 0.6 } }} />
        </Box>
        <Typography sx={{ color:c.textDark, fontSize:'1.1rem', fontWeight:700 }}>As {colorFilter === 'all' ? 'white' : colorFilter}</Typography>
      </Box>

      {/* Progress banner */}
      {isAnalyzing && (
        <Box sx={{ ...cardSx, mb: 2, py: 1.5, px: 2, bgcolor: `${c.accent}06`, border: `1px solid ${c.accent}15` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ color: c.textMuted, fontSize: '0.82rem', fontWeight: 600 }}>
              Analyzing games...
            </Typography>
            <Typography sx={{ color: c.accent, fontSize: '0.82rem', fontWeight: 700 }}>
              {analysisStatus.gamesAnalyzed}/{analysisStatus.gamesTotal}
            </Typography>
          </Box>
          <Box sx={{ height: 4, borderRadius: 2, bgcolor: `${c.textMuted}10`, overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${progressPct}%`, bgcolor: c.accent, borderRadius: 2, transition: 'width 0.5s ease' }} />
          </Box>
        </Box>
      )}

      {filteredBlunders.length === 0 ? (
        <Box sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: c.textDark, fontSize: '1.1rem', fontWeight: 800, mb: 1 }}>
            {isAnalyzing ? 'Analysis in progress' : `No ${category === 'blunder' ? 'blunders' : 'mistakes'} as ${colorFilter}`}
          </Typography>
          <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.7 }}>
            {isAnalyzing
              ? 'Positions will appear here as games are analyzed.'
              : 'Try switching the color filter or play more games.'}
          </Typography>
        </Box>
      ) : (
        <>
          <Typography sx={{ color: c.textDark, fontSize: '1.3rem', fontWeight: 800, mb: 0.5 }}>
            {category === 'blunder' ? 'Blunder Review' : 'Mistake Review'}
          </Typography>
          <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mb: 2, lineHeight: 1.6 }}>
            {filteredBlunders.length} position{filteredBlunders.length !== 1 ? 's' : ''} found. Select one to drill.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filteredBlunders.map((b, i) => {
              const isActive = blunderIndex === i;
              return (
                <Box key={i} onClick={() => { setBlunderIndex(i); setSelectedBlunder(b); setPuzzleFeedback(null); }} sx={{
                  px: 2, py: 1.5, borderRadius: '12px',
                  bgcolor: isActive ? `${c.accent}10` : `${c.textMuted}06`,
                  border: `1px solid ${isActive ? c.accent + '25' : c.textMuted + '10'}`,
                  cursor: 'pointer', transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: `${c.accent}08`, borderColor: `${c.accent}20` },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: isActive ? c.accent : c.textDark, fontSize: '0.95rem', fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.openingName || 'Unknown Opening'}
                    </Typography>
                    <Box sx={{
                      px: 1.2, py: 0.3, borderRadius: '8px', ml: 1, flexShrink: 0,
                      bgcolor: b.category === 'blunder' ? '#e74c3c15' : '#f39c1215',
                    }}>
                      <Typography sx={{
                        color: b.category === 'blunder' ? '#e74c3c' : '#f39c12',
                        fontSize: '0.8rem', fontWeight: 800,
                      }}>
                        -{(b.evalDrop / 100).toFixed(1)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography sx={{ color: c.textMuted, fontSize: '0.82rem' }}>
                      Move {Math.floor(b.moveNumber / 2) + 1}
                    </Typography>
                    {b.opponentUsername && (
                      <>
                        <Typography sx={{ color: c.textMuted, fontSize: '0.82rem' }}>·</Typography>
                        <Typography sx={{ color: c.textMuted, fontSize: '0.82rem' }}>vs {b.opponentUsername}</Typography>
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );

  // ═══ MAIN LAYOUT ═══
  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, alignItems: { lg: 'flex-start' } }}>

      {/* Left: blunder list */}
      <Box sx={{ flex: { xs: '1 1 100%', lg: '0 0 280px' }, maxWidth: { xs: '100%', lg: 280 }, minWidth: 0 }}>
        {blunderList}
      </Box>

      {/* Center: board */}
      <Box sx={{ flexShrink: 0, position: 'relative' }}>
        <InteractiveBoard
          c={c}
          fen={currentFen}
          flipped={flipped}
          pieceSet={pieceSet}
          boardTheme={boardTheme}
          selectedSquare={selectedBlunder ? selectedSquare : null}
          legalMoves={selectedBlunder ? legalMoves : []}
          lastMove={selectedBlunder ? lastMove : null}
          onSquareClick={selectedBlunder ? (ri, ci) => handleSquareClick(ri, ci) : () => {}}
          onDragStart={selectedBlunder ? handleDragStart : () => {}}
          onFlip={() => setFlipped(f => !f)}
          onGoTo={selectedBlunder ? goTo : () => {}}
          setArrows={setArrows}
          setHighlightedSquares={setHighlightedSquares}
          setPieceSet={setPieceSet}
          setBoardTheme={setBoardTheme}
          arrows={arrows}
          highlightedSquares={highlightedSquares}
          dragFrom={dragFrom}
          dragPos={dragPos}
          dragPiece={dragPiece}
          hoveredSquare={hoveredSquare}
          setHoveredSquare={setHoveredSquare}
          pendingPromotion={pendingPromotion}
          onPromotionChoice={handlePromotionChoice}
          setPendingPromotion={setPendingPromotion}
          positions={positionsArr}
          moveIndex={moveIndex}
        />
      </Box>

      {/* Right: feedback panel */}
      <Box sx={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>

        {!selectedBlunder ? (
          <Box sx={{ ...cardSx, textAlign: 'center', py: 4 }}>
            <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>Select a position to start drilling</Typography>
          </Box>
        ) : (
          <>
            {/* Turn indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...cardSx }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: turnColor === 'w' ? '#ECE2DB' : '#111115' }} />
                <Typography sx={{ color: c.textDark, fontSize: '1.1rem', fontWeight: 700 }}>{turnColor === 'w' ? 'White to move' : 'Black to move'}</Typography>
              </Box>
              <Typography sx={{ color: c.textMuted, fontSize: '0.85rem' }}>
                {blunderIndex + 1} / {filteredBlunders.length}
              </Typography>
            </Box>

            {/* Position context */}
            <Box sx={{ ...cardSx, bgcolor: `${c.accent}06`, border: `1px solid ${c.accent}12` }}>
              <Typography sx={{ color: c.textMuted, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>
                {selectedBlunder.category === 'blunder' ? 'Blunder' : 'Mistake'}
              </Typography>
              <Typography sx={{ color: c.textDark, fontSize: '1.15rem', fontWeight: 800, mb: 0.8 }}>
                {selectedBlunder.openingName || 'Unknown Opening'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>
                  Move {Math.floor(selectedBlunder.moveNumber / 2) + 1}
                </Typography>
                {selectedBlunder.opponentUsername && (
                  <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>
                    vs {selectedBlunder.opponentUsername}
                  </Typography>
                )}
                <Box sx={{ px: 1.2, py: 0.3, borderRadius: '8px', bgcolor: selectedBlunder.category === 'blunder' ? '#e74c3c12' : '#f39c1212' }}>
                  <Typography sx={{ color: selectedBlunder.category === 'blunder' ? '#e74c3c' : '#f39c12', fontSize: '0.85rem', fontWeight: 800 }}>
                    -{(selectedBlunder.evalDrop / 100).toFixed(1)} pawns
                  </Typography>
                </Box>
              </Box>
              {!puzzleFeedback && (
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mt: 1, lineHeight: 1.7 }}>
                  Find a better move than <strong style={{ color: '#e74c3c' }}>{selectedBlunder.userActualMove}</strong>
                </Typography>
              )}
            </Box>

            {/* Tiered feedback */}
            {puzzleFeedback && (
              <Box sx={{
                ...cardSx,
                bgcolor: puzzleFeedback.tier === 'best' ? '#27ae6010' : puzzleFeedback.tier === 'ok' ? '#f39c1210' : '#e74c3c10',
                border: `1px solid ${puzzleFeedback.tier === 'best' ? '#27ae6025' : puzzleFeedback.tier === 'ok' ? '#f39c1225' : '#e74c3c25'}`,
              }}>
                {/* Tier indicator */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  {puzzleFeedback.tier === 'best' && (
                    <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#27ae6020', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#27ae60" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </Box>
                  )}
                  {puzzleFeedback.tier === 'ok' && (
                    <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#f39c1220', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ color: '#f39c12', fontSize: '1rem', fontWeight: 900 }}>~</Typography>
                    </Box>
                  )}
                  {puzzleFeedback.tier === 'missed' && (
                    <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#e74c3c20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </Box>
                  )}
                  <Typography sx={{
                    color: puzzleFeedback.tier === 'best' ? '#27ae60' : puzzleFeedback.tier === 'ok' ? '#f39c12' : '#e74c3c',
                    fontSize: '1.15rem', fontWeight: 800,
                  }}>
                    {puzzleFeedback.tier === 'best' ? 'Best move!' : puzzleFeedback.tier === 'ok' ? 'Ok move' : 'Same mistake!'}
                  </Typography>
                </Box>

                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.7 }}>
                  You played <strong style={{ color: c.textDark }}>{puzzleFeedback.movePlayed}</strong>.
                  {puzzleFeedback.tier !== 'best' && (
                    <> The best move was <strong style={{ color: '#27ae60' }}>{puzzleFeedback.bestMove}</strong>.</>
                  )}
                  {puzzleFeedback.tier === 'missed' && (
                    <> In your game you played <strong style={{ color: '#e74c3c' }}>{puzzleFeedback.userActualMove}</strong> — the same move.</>
                  )}
                </Typography>

                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                  <Box onClick={handleUndo} sx={{ px: 3, py: 1.2, borderRadius: '12px', cursor: 'pointer', bgcolor: `${c.accent}12`, border: `1.5px solid ${c.accent}30`, display: 'inline-flex', alignItems: 'center', gap: 1, '&:hover': { bgcolor: `${c.accent}20` } }}>
                    <Typography sx={{ color: c.accent, fontSize: '0.95rem', fontWeight: 700 }}>Try again</Typography>
                  </Box>
                  {blunderIndex < filteredBlunders.length - 1 && (
                    <Box onClick={goToNext} sx={{ px: 3, py: 1.2, borderRadius: '12px', cursor: 'pointer', bgcolor: `${c.textMuted}08`, border: `1px solid ${c.textMuted}15`, display: 'inline-flex', alignItems: 'center', gap: 1, '&:hover': { bgcolor: `${c.textMuted}12` } }}>
                      <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', fontWeight: 700 }}>Next position</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Move history */}
            <Box sx={{ ...cardSx, maxHeight: { xs: 180, md: 260 }, overflow: 'auto' }}>
              <Typography sx={labelSx}>Moves</Typography>
              {movePairs.length === 0 ? (
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontStyle: 'italic' }}>Play a move to begin...</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {movePairs.map((pair) => {
                    const wIsOpening = pair.whiteIdx <= puzzleStartIdx;
                    const bIsOpening = pair.blackIdx <= puzzleStartIdx;
                    return (
                      <React.Fragment key={pair.num}>
                        {pair.whiteIdx === puzzleStartIdx + 1 && <Box sx={{ width: '100%', borderTop: `1px dashed ${c.textMuted}30`, my: 0.8 }} />}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <Typography sx={{ color: `${c.textMuted}80`, fontSize: '0.95rem', fontWeight: 600, opacity: wIsOpening ? 0.5 : 1 }}>{pair.num}.</Typography>
                          <Box onClick={() => goTo(pair.whiteIdx)} sx={{ px: 1, py: 0.3, borderRadius: '6px', cursor: 'pointer', bgcolor: moveIndex === pair.whiteIdx ? `${c.accent}25` : 'transparent', '&:hover': { bgcolor: `${c.accent}12` } }}>
                            <Typography sx={{ color: moveIndex === pair.whiteIdx ? c.accent : wIsOpening ? c.textMuted : c.textDark, fontSize: '1rem', fontWeight: moveIndex === pair.whiteIdx ? 700 : 500, opacity: wIsOpening ? 0.5 : 1 }}>{pair.white.san}</Typography>
                          </Box>
                          {pair.black && (
                            <>
                              {pair.blackIdx === puzzleStartIdx + 1 && <Box sx={{ width: '100%', borderTop: `1px dashed ${c.textMuted}30`, my: 0.8 }} />}
                              <Box onClick={() => goTo(pair.blackIdx)} sx={{ px: 1, py: 0.3, borderRadius: '6px', cursor: 'pointer', bgcolor: moveIndex === pair.blackIdx ? `${c.accent}25` : 'transparent', '&:hover': { bgcolor: `${c.accent}12` } }}>
                                <Typography sx={{ color: moveIndex === pair.blackIdx ? c.accent : bIsOpening ? c.textMuted : c.textDark, fontSize: '1rem', fontWeight: moveIndex === pair.blackIdx ? 700 : 500, opacity: bIsOpening ? 0.5 : 1 }}>{pair.black.san}</Typography>
                              </Box>
                            </>
                          )}
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Session stats */}
            <Box sx={cardSx}>
              <Typography sx={labelSx}>Session Stats</Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Typography sx={{ color: c.textDark, fontSize: '1.8rem', fontWeight: 800 }}>{pStats.total}</Typography>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.85rem' }}>Attempted</Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: '#27ae60', fontSize: '1.8rem', fontWeight: 800 }}>{pStats.best}</Typography>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.85rem' }}>Best</Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: '#f39c12', fontSize: '1.8rem', fontWeight: 800 }}>{pStats.ok}</Typography>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.85rem' }}>Ok</Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: '#e74c3c', fontSize: '1.8rem', fontWeight: 800 }}>{pStats.missed}</Typography>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.85rem' }}>Missed</Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
